import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  FiArrowLeft, 
  FiEye, 
  FiMousePointer, 
  FiMessageSquare,
  FiBookmark, 
  FiShare2, 
  FiTrendingUp, 
  FiTrendingDown,
  FiMinus,
  FiEdit,
  FiAlertCircle,
  FiInfo,
  FiBarChart2,
  FiRefreshCw,
  FiClock,
  FiCheckCircle,
  FiActivity
} from 'react-icons/fi';
import { BiCategory } from 'react-icons/bi';
import Badge from '../../../components/common/Badge';
import Button from '../../../components/common/Button';
import Loading from '../../../components/common/Loading';
import { useAuth } from '../../../context/AuthContext';
import { 
  getListing,
  getListingPerformance,
  getTrackingAnalytics,
  getPerformanceComparison,
  getListingBoosts
} from '../../../services/api';

const PerformanceScreen = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [listing, setListing] = useState(null);
  const [performance, setPerformance] = useState(null);
  const [trackingData, setTrackingData] = useState(null);
  const [comparisonData, setComparisonData] = useState(null);
  const [boostData, setBoostData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [timeRange, setTimeRange] = useState('7d');

  useEffect(() => {
    fetchAllPerformanceData();
  }, [id, timeRange]);

  const fetchAllPerformanceData = async () => {
    try {
      setLoading(true);
      setError(null);
      // console.log('📊 Fetching performance data for listing:', id);

      const [
        listingResponse,
        performanceResponse,
        trackingResponse,
        comparisonResponse,
        boostResponse
      ] = await Promise.allSettled([
        getListing(id),
        getListingPerformance(id),
        getTrackingAnalytics(id),
        getPerformanceComparison(id, timeRange),
        getListingBoosts(id)
      ]);

      // Handle listing response
      if (listingResponse.status === 'fulfilled' && listingResponse.value.data) {
        setListing(listingResponse.value.data);
      } else {
        // console.warn('⚠️ Failed to fetch listing:', listingResponse.reason);
        setListing({
          listing_id: parseInt(id),
          title: 'Listing ' + id,
          price: 0,
          status: 'active'
        });
      }

      // Handle performance response
      if (performanceResponse.status === 'fulfilled' && performanceResponse.value.success) {
        setPerformance(performanceResponse.value);
      } else {
        // console.warn('⚠️ Failed to fetch performance:', performanceResponse.reason);
      }

      // Handle tracking response
      if (trackingResponse.status === 'fulfilled' && trackingResponse.value.success) {
        setTrackingData(trackingResponse.value.data);
      } else {
        // console.warn('⚠️ Failed to fetch tracking analytics:', trackingResponse.reason);
      }

      // Handle comparison response
      if (comparisonResponse.status === 'fulfilled' && comparisonResponse.value.success) {
        const comparisonData = comparisonResponse.value.data;
        if (comparisonData.performance_error) {
          // console.warn('⚠️ Performance data has SQL error:', comparisonData.performance_error);
        }
        setComparisonData(comparisonData);
      } else {
        // console.warn('⚠️ Failed to fetch performance comparison:', comparisonResponse.reason);
      }

      // Handle boost response
      if (boostResponse.status === 'fulfilled' && boostResponse.value.success) {
        setBoostData(boostResponse.value);
      } else {
        // console.warn('⚠️ Failed to fetch boost data:', boostResponse.reason);
      }

    } catch (error) {
      // console.error('❌ Error loading performance data:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const calculateRealMetrics = () => {
    if (performance && performance.success) {
      const perf = performance.performance || performance.data || {};
      const engagementBreakdown = trackingData?.engagements || [];
      const engagementMetrics = {
        clicks: perf.total_clicks || 0,
        messages: engagementBreakdown.find(e => e.engagement_type === 'message_sent')?.count || 0,
        saves: engagementBreakdown.find(e => e.engagement_type === 'favorite')?.count || 0,
        shares: engagementBreakdown.find(e => e.engagement_type === 'listing_shared')?.count || 0
      };

      const comparison = comparisonData || {};
      const categoryAvgViews = comparison.category_averages?.avg_views || Math.round((perf.view_count || 0) * 0.6);
      
      const performanceRating = (perf.view_count || 0) > categoryAvgViews ? 'above_avg' : 
                              (perf.view_count || 0) < categoryAvgViews * 0.5 ? 'below_avg' : 'avg';

      return {
        views: {
          total: perf.view_count || 0,
          daily: perf.daily_performance ? perf.daily_performance.map(day => day.impressions || 0) : [12, 18, 15, 22, 28, 35, 42]
        },
        engagement: engagementMetrics,
        conversion: {
          views_to_contact: '0%',
          contact_to_order: '0%',
          total_orders: perf.total_orders || 0,
          revenue: perf.total_revenue || 0
        },
        comparison: {
          category_avg_views: categoryAvgViews,
          category_avg_orders: comparison.category_averages?.avg_orders || 2,
          performance: performanceRating
        },
        boost: {
          has_active_boost: perf.has_active_boost || (boostData?.data && boostData.data.length > 0),
          active_boost: boostData?.data ? boostData.data[0] : null
        },
        metrics: {
          impressions: perf.total_impressions || 0,
          clicks: perf.total_clicks || 0,
          ctr: perf.click_through_rate || 0,
          engagements: perf.total_engagements || 0,
          engagement_rate: perf.engagement_rate || 0
        }
      };
    }

    // Fallback logic
    if (trackingData) {
      const overview = trackingData.overview || {};
      const engagements = trackingData.engagements || [];
      
      const engagementMetrics = {
        clicks: overview.total_clicks || 0,
        messages: engagements.find(e => e.engagement_type === 'message_sent')?.count || 0,
        saves: engagements.find(e => e.engagement_type === 'favorite')?.count || 0,
        shares: engagements.find(e => e.engagement_type === 'listing_shared')?.count || 0
      };

      return {
        views: {
          total: overview.total_impressions || 0,
          daily: trackingData.daily_trends ? trackingData.daily_trends.map(day => day.impressions || 0) : [12, 18, 15, 22, 28, 35, 42]
        },
        engagement: engagementMetrics,
        conversion: {
          views_to_contact: '0%',
          contact_to_order: '0%',
          total_orders: 0,
          revenue: 0
        },
        comparison: {
          category_avg_views: 50,
          category_avg_orders: 2,
          performance: 'avg'
        },
        boost: {
          has_active_boost: boostData?.data && boostData.data.length > 0,
          active_boost: boostData?.data ? boostData.data[0] : null
        },
        metrics: {
          impressions: overview.total_impressions || 0,
          clicks: overview.total_clicks || 0,
          ctr: overview.click_rate || 0,
          engagements: engagementMetrics.messages + engagementMetrics.saves + engagementMetrics.shares,
          engagement_rate: 0
        }
      };
    }

    return {
      views: {
        total: 0,
        daily: [0, 0, 0, 0, 0, 0, 0]
      },
      engagement: {
        clicks: 0,
        messages: 0,
        saves: 0,
        shares: 0
      },
      conversion: {
        views_to_contact: '0%',
        contact_to_order: '0%',
        total_orders: 0,
        revenue: 0
      },
      comparison: {
        category_avg_views: 50,
        category_avg_orders: 2,
        performance: 'below_avg'
      },
      boost: {
        has_active_boost: false,
        active_boost: null
      },
      metrics: {
        impressions: 0,
        clicks: 0,
        ctr: 0,
        engagements: 0,
        engagement_rate: 0
      }
    };
  };

  const getPerformanceColor = (type) => {
    switch (type) {
      case 'above_avg':
        return { color: 'text-green-600', bgColor: 'bg-green-50', borderColor: 'border-green-200', icon: FiTrendingUp };
      case 'below_avg':
        return { color: 'text-red-600', bgColor: 'bg-red-50', borderColor: 'border-red-200', icon: FiTrendingDown };
      default:
        return { color: 'text-yellow-600', bgColor: 'bg-yellow-50', borderColor: 'border-yellow-200', icon: FiMinus };
    }
  };

  const handleBack = () => {
    navigate(-1);
  };

  const handleRefresh = () => {
    fetchAllPerformanceData();
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loading size="large" text="Loading performance data..." />
      </div>
    );
  }

  if (error && !listing) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="text-center max-w-md">
          <div className="w-24 h-24 mx-auto mb-6 bg-orange-100 rounded-full flex items-center justify-center">
            <FiActivity className="w-12 h-12 text-orange-500" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-3">
            Error Loading Data
          </h2>
          <p className="text-gray-600 mb-8">
            {error || 'We couldn\'t load the performance data for this listing.'}
          </p>
          <Button 
            onClick={handleBack}
            variant="primary"
            size="large"
            className="px-8"
          >
            Go Back
          </Button>
        </div>
      </div>
    );
  }

  const performanceMetrics = calculateRealMetrics();
  const hasActiveBoost = performanceMetrics?.boost?.has_active_boost;
  const recommendations = comparisonData?.recommendations || [];
  const performanceStyle = getPerformanceColor(performanceMetrics.comparison.performance);
  const PerformanceIcon = performanceStyle.icon;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={handleBack}
                className="p-2 hover:bg-gray-100 rounded-lg transition"
              >
                <FiArrowLeft className="w-5 h-5 text-gray-600" />
              </button>
              <div>
                <h1 className="text-xl font-bold text-gray-900">
                  Performance Analytics
                </h1>
                <p className="text-sm text-gray-500">
                  Track and analyze your listing's performance
                </p>
              </div>
            </div>
            <button
              onClick={handleRefresh}
              className="p-2 hover:bg-gray-100 rounded-lg transition"
            >
              <FiRefreshCw className="w-5 h-5 text-gray-600" />
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Listing Info Card */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 mb-6">
          <div className="p-6">
            <div className="flex items-start justify-between mb-6">
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-2">
                  <h2 className="text-lg font-semibold text-gray-900 truncate">
                    {listing?.title || 'Loading...'}
                  </h2>
                  {hasActiveBoost && (
                    <Badge 
                      type="boost" 
                      className="bg-orange-100 text-orange-800 border-orange-200"
                    >
                      <FiTrendingUp className="w-3 h-3 mr-1" />
                      BOOSTED
                    </Badge>
                  )}
                </div>
                <p className="text-2xl font-bold text-orange-600 mb-1">
                  ₦{parseFloat(listing?.price || 0).toLocaleString()}
                </p>
                <p className="text-sm text-gray-500">
                  ID: {id} • {listing?.status ? listing.status.charAt(0).toUpperCase() + listing.status.slice(1) : 'Active'}
                </p>
              </div>
            </div>

            {/* Controls Row */}
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
              <div className="flex items-center gap-6">
                {comparisonData?.performance_score && (
                  <div className="text-center">
                    <p className="text-sm text-gray-500 mb-1">Performance Score</p>
                    <div className="w-16 h-16 rounded-full bg-orange-500 flex items-center justify-center mx-auto">
                      <span className="text-xl font-bold text-white">
                        {comparisonData.performance_score}
                      </span>
                    </div>
                  </div>
                )}

                <div className={`px-4 py-2 rounded-lg flex items-center gap-2 ${performanceStyle.bgColor} ${performanceStyle.borderColor} border`}>
                  <PerformanceIcon className={`w-4 h-4 ${performanceStyle.color}`} />
                  <span className={`text-sm font-medium ${performanceStyle.color}`}>
                    {performanceMetrics.comparison.performance === 'above_avg' ? 'Above Average' : 
                     performanceMetrics.comparison.performance === 'below_avg' ? 'Below Average' : 'Average'}
                  </span>
                </div>
              </div>

              <div>
                <p className="text-sm text-gray-500 mb-2">Time Range</p>
                <div className="flex bg-gray-100 rounded-lg p-1">
                  {['7d', '30d', '90d'].map((range) => (
                    <button
                      key={range}
                      onClick={() => setTimeRange(range)}
                      className={`px-4 py-2 rounded-md text-sm font-medium transition ${
                        timeRange === range 
                          ? 'bg-white shadow-sm text-orange-600' 
                          : 'text-gray-600 hover:text-gray-900'
                      }`}
                    >
                      {range === '7d' ? '7 Days' : range === '30d' ? '30 Days' : '90 Days'}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {error && (
              <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg flex items-start gap-3">
                <FiAlertCircle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-yellow-800">
                  Some data may be limited. {error}
                </p>
              </div>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column - Key Metrics */}
          <div className="lg:col-span-2 space-y-6">
            {/* Key Metrics Summary */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-6">Key Metrics</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="bg-gray-50 rounded-lg p-5">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
                      <FiEye className="w-5 h-5 text-orange-600" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-gray-900">
                        {performanceMetrics.views.total.toLocaleString()}
                      </p>
                      <p className="text-sm text-gray-500">Views</p>
                    </div>
                  </div>
                  <p className="text-sm text-blue-600 font-medium">
                    {performanceMetrics.metrics.ctr}% CTR
                  </p>
                </div>

                <div className="bg-gray-50 rounded-lg p-5">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                      <FiMousePointer className="w-5 h-5 text-blue-600" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-gray-900">
                        {performanceMetrics.metrics.clicks.toLocaleString()}
                      </p>
                      <p className="text-sm text-gray-500">Clicks</p>
                    </div>
                  </div>
                  <p className="text-sm text-blue-600 font-medium">
                    {performanceMetrics.metrics.engagements} engagements
                  </p>
                </div>

                <div className="bg-gray-50 rounded-lg p-5">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                      <FiMessageSquare className="w-5 h-5 text-green-600" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-gray-900">
                        {performanceMetrics.engagement.messages}
                      </p>
                      <p className="text-sm text-gray-500">Messages</p>
                    </div>
                  </div>
                  <p className="text-sm text-blue-600 font-medium">
                    {performanceMetrics.conversion.total_orders} orders
                  </p>
                </div>
              </div>
            </div>

            {/* Engagement Breakdown */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-6">Engagement Breakdown</h2>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                      <FiMousePointer className="w-4 h-4 text-blue-600" />
                    </div>
                    <div>
                      <p className="text-xl font-bold text-gray-900">
                        {performanceMetrics.engagement.clicks}
                      </p>
                      <p className="text-xs text-gray-500">Profile Clicks</p>
                    </div>
                  </div>
                </div>

                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center">
                      <FiBookmark className="w-4 h-4 text-purple-600" />
                    </div>
                    <div>
                      <p className="text-xl font-bold text-gray-900">
                        {performanceMetrics.engagement.saves}
                      </p>
                      <p className="text-xs text-gray-500">Saves</p>
                    </div>
                  </div>
                </div>

                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-8 h-8 bg-yellow-100 rounded-lg flex items-center justify-center">
                      <FiShare2 className="w-4 h-4 text-yellow-600" />
                    </div>
                    <div>
                      <p className="text-xl font-bold text-gray-900">
                        {performanceMetrics.engagement.shares}
                      </p>
                      <p className="text-xs text-gray-500">Shares</p>
                    </div>
                  </div>
                </div>

                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-8 h-8 bg-orange-100 rounded-lg flex items-center justify-center">
                      <FiMessageSquare className="w-4 h-4 text-orange-600" />
                    </div>
                    <div>
                      <p className="text-xl font-bold text-gray-900">
                        {performanceMetrics.engagement.messages}
                      </p>
                      <p className="text-xs text-gray-500">Messages</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Market Comparison */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-lg font-semibold text-gray-900">Market Comparison</h2>
                <div className={`px-3 py-1 rounded-full flex items-center gap-2 ${performanceStyle.bgColor} ${performanceStyle.borderColor} border`}>
                  <PerformanceIcon className={`w-3 h-3 ${performanceStyle.color}`} />
                  <span className={`text-xs font-medium ${performanceStyle.color}`}>
                    {performanceMetrics.comparison.performance === 'above_avg' ? 'Above Average' : 
                     performanceMetrics.comparison.performance === 'below_avg' ? 'Below Average' : 'Average'}
                  </span>
                </div>
              </div>

              <div className="space-y-6">
                <div>
                  <div className="flex justify-between mb-2">
                    <span className="text-sm text-gray-600">Your Views</span>
                    <span className="font-medium text-gray-900">
                      {performanceMetrics.views.total.toLocaleString()}
                    </span>
                  </div>
                  <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                    <div 
                      className={`h-full rounded-full ${
                        performanceMetrics.views.total > performanceMetrics.comparison.category_avg_views 
                          ? 'bg-green-500' 
                          : 'bg-red-500'
                      }`}
                      style={{ 
                        width: `${Math.min((performanceMetrics.views.total / performanceMetrics.comparison.category_avg_views) * 100, 100)}%` 
                      }}
                    />
                  </div>
                </div>

                <div>
                  <div className="flex justify-between mb-2">
                    <span className="text-sm text-gray-600">Category Average</span>
                    <span className="font-medium text-gray-900">
                      {performanceMetrics.comparison.category_avg_views.toLocaleString()}
                    </span>
                  </div>
                  <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                    <div className="h-full w-full bg-gray-400 rounded-full" />
                  </div>
                </div>

                <div className="flex items-center gap-2 text-sm text-gray-500">
                  <BiCategory className="w-4 h-4" />
                  <span>Based on {listing?.category_name || 'your listing category'}</span>
                </div>
              </div>
            </div>

            {/* Improvement Tips */}
            {recommendations.length > 0 && (
              <div className="bg-green-50 border border-green-200 rounded-xl p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                    <FiInfo className="w-5 h-5 text-green-600" />
                  </div>
                  <h3 className="text-lg font-semibold text-green-900">Improvement Tips</h3>
                </div>
                <div className="space-y-4">
                  {recommendations.slice(0, 3).map((rec, index) => (
                    <div key={index} className="flex items-start gap-3">
                      {rec.priority === 'high' ? (
                        <FiAlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                      ) : (
                        <FiInfo className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                      )}
                      <div>
                        <p className="font-medium text-green-900">{rec.title}</p>
                        <p className="text-sm text-green-800 mt-1">{rec.message}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Right Column - Actions and Stats */}
          <div className="space-y-6">
            {/* Action Buttons */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Listing Actions</h2>
              <div className="space-y-3">
                {!hasActiveBoost ? (
                  <Button
                    onClick={() => navigate(`/store/boost-package/${id}`)}
                    variant="primary"
                    fullWidth
                    icon={<FiTrendingUp className="w-5 h-5" />}
                    iconPosition="left"
                    className="justify-center"
                  >
                    Boost This Listing
                  </Button>
                ) : (
                  <Button
                    onClick={() => navigate('/store/boost-history')}
                    variant="primary"
                    fullWidth
                    icon={<FiBarChart2 className="w-5 h-5" />}
                    iconPosition="left"
                    className="justify-center"
                  >
                    View Boost Analytics
                  </Button>
                )}

                <Button
                  onClick={() => navigate(`/store/listings/edit-listing/${id}`)}
                  variant="outline"
                  fullWidth
                  icon={<FiEdit className="w-5 h-5" />}
                  iconPosition="left"
                  className="justify-center"
                >
                  Edit Listing
                </Button>
              </div>
            </div>

            {/* Performance Stats */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Performance Stats</h2>
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-gray-600">Impressions</span>
                  <span className="font-semibold text-gray-900">
                    {performanceMetrics.metrics.impressions.toLocaleString()}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-gray-600">Click Rate</span>
                  <span className="font-semibold text-gray-900">
                    {performanceMetrics.metrics.ctr}%
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-gray-600">Engagement Rate</span>
                  <span className="font-semibold text-gray-900">
                    {performanceMetrics.metrics.engagement_rate}%
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-gray-600">Total Orders</span>
                  <span className="font-semibold text-gray-900">
                    {performanceMetrics.conversion.total_orders}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-gray-600">Revenue</span>
                  <span className="font-semibold text-orange-600">
                    ₦{parseFloat(performanceMetrics.conversion.revenue || 0).toLocaleString()}
                  </span>
                </div>
              </div>
            </div>

            {/* Conversion Metrics */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Conversion Metrics</h2>
              <div className="space-y-4">
                <div>
                  <div className="flex justify-between mb-1">
                    <span className="text-sm text-gray-600">Views to Contact</span>
                    <span className="text-sm font-medium text-gray-900">
                      {performanceMetrics.conversion.views_to_contact}
                    </span>
                  </div>
                  <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-blue-500 rounded-full"
                      style={{ width: parseFloat(performanceMetrics.conversion.views_to_contact) || '0%' }}
                    />
                  </div>
                </div>
                <div>
                  <div className="flex justify-between mb-1">
                    <span className="text-sm text-gray-600">Contact to Order</span>
                    <span className="text-sm font-medium text-gray-900">
                      {performanceMetrics.conversion.contact_to_order}
                    </span>
                  </div>
                  <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-green-500 rounded-full"
                      style={{ width: parseFloat(performanceMetrics.conversion.contact_to_order) || '0%' }}
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Additional Info */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Additional Info</h2>
              <div className="space-y-3 text-sm">
                <div className="flex items-center justify-between">
                  <span className="text-gray-600">Data Updated</span>
                  <span className="font-medium text-gray-900">
                    <FiClock className="w-3 h-3 inline mr-1" />
                    Just now
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-gray-600">Time Range</span>
                  <span className="font-medium text-gray-900">
                    {timeRange === '7d' ? '7 Days' : timeRange === '30d' ? '30 Days' : '90 Days'}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-gray-600">Boost Status</span>
                  <Badge 
                    type={hasActiveBoost ? 'success' : 'default'}
                    className={hasActiveBoost ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}
                  >
                    {hasActiveBoost ? 'Active' : 'Not Active'}
                  </Badge>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PerformanceScreen;